import type { LayoutDocument, LayoutElement } from "@private-fiction/story-core";

const boundedCount = (value: number | undefined, fallback = 1) => Number.isFinite(value)
  ? Math.max(1, Math.min(12, Math.trunc(value!))) : fallback;

/** One phone composition is used in every host, independent of the browser viewport. */
export function phoneLayout(element: { responsive?: LayoutElement["responsive"] }) {
  const phone = element.responsive?.phone;
  return {
    columns: boundedCount(phone?.columns),
    span: boundedCount(phone?.span),
    order: Number.isFinite(phone?.order) ? phone!.order! : 0,
    hidden: phone?.hidden === true,
  };
}

/** Reduce dense grids only when their own panel cannot fit readable cells. */
export function fittedLayoutColumns(requested: number, width: number, gap = 14, minimum = 120): number {
  const count = boundedCount(requested);
  if (!Number.isFinite(width) || width <= 0) return 1;
  return Math.max(1, Math.min(count, Math.floor((width + gap) / (minimum + gap))));
}

export function phoneLayoutDocument(document: LayoutDocument): LayoutDocument {
  const next = structuredClone(document);
  for (const element of Object.values(next.elements)) {
    const phone = phoneLayout(element);
    if (element.responsive || element.type === "grid") {
      element.responsive = { phone };
    }
    if (element.type === "grid") {
      element.columns = phone.columns;
      element.props = { ...element.props, columns: phone.columns };
    }
  }
  return next;
}

export const layoutAspectRatios: Record<string, string> = {
  "2:3": "2 / 3", "1:1": "1", "3:4": "3 / 4",
  video: "16 / 9", square: "1", portrait: "3 / 4", tall: "9 / 16",
  ultrawide: "32 / 9", ultratall: "9 / 32",
};

/** Explicit layout positioning wins over the source image's normalized crop. */
export function layoutImagePosition(image: unknown, explicit?: string): string {
  if (explicit) return explicit;
  if (!image || typeof image !== "object" || !("focalPoint" in image)) return "center";
  const point = image.focalPoint;
  if (!point || typeof point !== "object" || !("x" in point) || !("y" in point)) return "center";
  if (typeof point.x !== "number" || typeof point.y !== "number" || !Number.isFinite(point.x) || !Number.isFinite(point.y)) return "center";
  const percent = (value: number) => Math.max(0, Math.min(1, value)) * 100;
  return `${percent(point.x)}% ${percent(point.y)}%`;
}
